import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  isNativeApp,
  getCapacitorPlugin,
  closeInAppBrowser,
  NATIVE_OAUTH_REDIRECT_SCHEME,
  NATIVE_OAUTH_REDIRECT_HOST
} from '../utils/nativeApp';
import { logWarn } from '../utils/logger';

const EXPECTED_PROTOCOL = `${NATIVE_OAUTH_REDIRECT_SCHEME}:`;
// Tenant-Slug: nur Kleinbuchstaben, Ziffern und Bindestrich (wie in der
// restlichen Tenant-Slug-Validierung des Frontends üblich).
const SLUG_PATH_PATTERN = /^\/[a-z0-9-]+$/;

/**
 * Parst eine Deep-Link-URL und liefert Tenant-Slug + Query-String, falls sie
 * exakt der erwarteten OAuth-Rückkehr-Form entspricht, sonst null.
 *
 * WHATWG-URL-Parsing von Custom-Schemes (nicht in der "special scheme"-Liste
 * der URL-Spec) ist für die hier verwendete `scheme://host/path`-Form
 * wohldefiniert — verifiziert: `new URL('de.rentinger.app://oauth-callback/mvg?x=1')`
 * liefert protocol='de.rentinger.app:', host='oauth-callback',
 * pathname='/mvg', searchParams wie erwartet. Trotzdem wird hier jeder Teil
 * explizit geprüft statt der Struktur blind zu vertrauen.
 *
 * @param {string} rawUrl
 * @returns {{ slug: string, search: string } | null}
 */
export const parseOAuthDeepLink = (rawUrl) => {
  if (!rawUrl || typeof rawUrl !== 'string') return null;

  let parsed;
  try {
    parsed = new URL(rawUrl);
  } catch {
    return null;
  }

  if (parsed.protocol !== EXPECTED_PROTOCOL) return null;
  if (parsed.host !== NATIVE_OAUTH_REDIRECT_HOST) return null;
  if (!SLUG_PATH_PATTERN.test(parsed.pathname)) return null;

  return { slug: parsed.pathname.slice(1), search: parsed.search };
};

/**
 * Rendert nichts (null). In der nativen App lauscht diese Komponente auf
 * eingehende Custom-Scheme-Deep-Links
 * (de.rentinger.app://oauth-callback/{tenantSlug}?...) — die Rückkehr aus dem
 * System-/In-App-Browser nach dem OAuth-Flow (siehe utils/oauthStart.js) —
 * und navigiert intern auf die bestehende OAuthCallback-Route, die den Code
 * genau wie im Web verarbeitet.
 *
 * Deckt zwei Fälle ab:
 *  - App bereits im Vordergrund/Hintergrund aktiv: 'appUrlOpen'-Event.
 *  - App wurde per Deep-Link frisch gestartet: App.getLaunchUrl() (einmalig
 *    beim Mount abgefragt).
 *
 * Im Web-Build (isNativeApp() === false) ist die Komponente ein No-Op.
 * Muss innerhalb von <Router> gemountet werden (nutzt useNavigate).
 */
const DeepLinkHandler = () => {
  const navigate = useNavigate();
  const lastHandledUrlRef = useRef(null);

  useEffect(() => {
    if (!isNativeApp()) return undefined;

    const App = getCapacitorPlugin('App');
    // Consistent with nativeApp.js's openExternal/closeInAppBrowser style:
    // never assume plugin methods exist just because the plugin object does
    // (older Capacitor plugin build, partially mocked test double, ...).
    if (!App) return undefined;

    let active = true;
    let listenerHandle = null;

    const handle = (url) => {
      if (!url || url === lastHandledUrlRef.current) return;
      lastHandledUrlRef.current = url;

      const link = parseOAuthDeepLink(url);
      if (!link) {
        logWarn('[DeepLinkHandler] Unbekannter/ungültiger Deep-Link ignoriert:', url);
        return;
      }

      navigate(`/${link.slug}/auth/oauth/callback${link.search}`, { replace: true });
      closeInAppBrowser();
    };

    // App.addListener's return value is tolerated in BOTH shapes: the npm
    // package types (and this project's E2E stub, before this fix) declare
    // Promise<PluginListenerHandle>, but the REAL injected Capacitor 7
    // runtime (window.Capacitor.Plugins.App) returns the listener handle
    // SYNCHRONOUSLY — confirmed via a device logcat crash
    // ("n.addListener(...).then is not a function") once this ran on an
    // actual emulator. Treating the return value as "maybe a promise, maybe
    // already the handle" covers both without depending on which one a given
    // Capacitor/plugin version actually implements.
    if (typeof App.addListener === 'function') {
      try {
        const addListenerReturn = App.addListener('appUrlOpen', (data) => handle(data && data.url));
        if (addListenerReturn && typeof addListenerReturn.then === 'function') {
          addListenerReturn
            .then((handleResult) => {
              if (active) {
                listenerHandle = handleResult;
              } else {
                // Component already unmounted before the listener registration
                // resolved — remove it immediately instead of leaking it.
                handleResult?.remove?.();
              }
            })
            .catch(() => {
              // Plugin not fully ready / registration failed — nothing to clean up.
            });
        } else {
          listenerHandle = addListenerReturn;
        }
      } catch (e) {
        logWarn('[DeepLinkHandler] App.addListener fehlgeschlagen:', e);
      }
    } else {
      logWarn('[DeepLinkHandler] App.addListener nicht verfügbar — appUrlOpen wird nicht überwacht.');
    }

    // Same "promise or plain value" tolerance for getLaunchUrl — and its
    // resolved/returned result can itself be undefined/null or carry no url
    // (no cold start via deep link), which is the normal, non-error case.
    if (typeof App.getLaunchUrl === 'function') {
      try {
        const getLaunchUrlReturn = App.getLaunchUrl();
        if (getLaunchUrlReturn && typeof getLaunchUrlReturn.then === 'function') {
          getLaunchUrlReturn
            .then((result) => {
              if (active && result?.url) {
                handle(result.url);
              }
            })
            .catch(() => {
              // getLaunchUrl kann scheitern (kein Cold-Start über Deep-Link) —
              // unkritisch, appUrlOpen deckt den laufenden Betrieb ab.
            });
        } else if (active && getLaunchUrlReturn?.url) {
          handle(getLaunchUrlReturn.url);
        }
      } catch (e) {
        // Unkritisch — appUrlOpen deckt den laufenden Betrieb ab.
      }
    }
    // else: ältere Plugin-Version ohne getLaunchUrl — unkritisch, appUrlOpen
    // deckt den laufenden Betrieb ab; nur der Cold-Start-Fall wird verpasst.

    return () => {
      active = false;
      listenerHandle?.remove?.();
    };
  }, [navigate]);

  return null;
};

export default DeepLinkHandler;
